import {
  Input,
  Box,
  Text,
  VStack,
  Button,
  Image as ChakraImage,
  HStack,
  Textarea,
  Progress,
  useToast,
  RadioGroup,
  Radio,
  Stack,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { useMemo, useRef, useState, useEffect } from "react";
import { Controller, set, useForm } from "react-hook-form";
import { useLoading } from "../../../../hooks/form";
import FileUploader from "../FileUploader";
import Section from "../Section";
import { validateAspectRatio, validateMultipleAspectRatios } from "../util";
import MediaDisplay from "../MediaDisplay";
import transform, { AdCreativeFormValues } from "./transform/adcreative";
import { StepProps } from "./CreateCampaignWizard";
import WhiteBgInput from "./WhiteBgInput";
import ValidatedField from "./ValidatedField";
import { AdCreativeFormData, AdCreativeFormSchema } from "./forms/adcreative";
import { yupResolver } from "@hookform/resolvers/yup";

const requiredImagePlaceholders = [
  {
    ratio: [{ min: "1/1", max: "1/1" }],
    title: "Square (*)",
    ratioName: "square",
    description: "instagram post, explore, facebook feed",
    minWidth: 1440,
    type: null,
    required: true,
    minHeight: 1440,
    url: undefined,
  },
  {
    ratio: [
      // { min: "1/1.91", max: "9/16" },
      { min: "4/5", max: "191/100" },
    ],
    title: "Portrait (*)",
    ratioName: "portrait",
    description: "instagram story",
    required: true,
    type: null,
    minWidth: 1152,
    minHeight: 1440,
    url: undefined,
  },
];

const AdCreativeForm = ({
  onCreateAdCreative,
  adset,
  ad,
  back,
  campaign,
  instagramAccounts = [],
  pages,
  currency,
  loading: _outsideLoading,
  createUploadUrl,
}: StepProps) => {
  const toast = useToast();
  const uploadImageRef = useRef<HTMLInputElement>(null);
  const uploadVideoRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [requiredImages, setRequiredImages] = useState(
    requiredImagePlaceholders
  );
  const [loading, setLoading] = useLoading({
    image: false,
    audience: false,
    targeting: false,
  });

  const {
    register,
    reset,
    control,
    formState: { errors, isValid, isDirty, touchedFields, dirtyFields },
    trigger,
    handleSubmit,
    getValues,
    setValue,
    watch,
  } = useForm<AdCreativeFormData>({
    resolver: yupResolver(AdCreativeFormSchema),
    mode: "onTouched",
    criteriaMode: "all",
    reValidateMode: "onChange",
    shouldFocusError: true,
    delayError: 0,
    values: transform.input({
      ad,
      campaign,
      adset,
      pages,
      instagramAccounts,
    }),
  });

  // Trigger validation on mount and when media type changes
  useEffect(() => {
    trigger();
  }, [trigger, watch("media_type"), watch("images"), watch("videos")]);

  console.log({
    errors,
    isValid,
    isDirty,
    values: getValues(),
    touchedFields,
    dirtyFields,
  });

  const images = watch("images");
  const videos = watch("videos");
  const media_type = watch("media_type");
  const hasAnyVideo = Object.values(videos).some((v) => v.length > 0);
  const hasAnyImage = Object.values(images).some((v) => v.length > 0);

  const _onSubmit = async (data: AdCreativeFormValues) => {
    setRequiredImages(requiredImagePlaceholders);
    return onCreateAdCreative(data).finally(() => {
      reset();
    });
  };

  const ratios = useMemo(() => {
    if (media_type === "carousel") {
      return ["square"];
    }

    return ["square", "portrait"];
  }, [requiredImages, media_type]);

  console.log({
    videos,
  });

  const isLoading =
    _outsideLoading.adsets ||
    _outsideLoading.campaigns ||
    _outsideLoading.audiences ||
    _outsideLoading.pixels ||
    _outsideLoading.publish ||
    _outsideLoading.pages ||
    _outsideLoading.instagramAccounts;

  return (
    <VStack w="full" textAlign={"left"} py={8} gap={8}>
      <Section
        title={`${ad ? "Editing" : "Creating"} creative - ${
          ad?.name ? `${ad.name} in ${adset?.name}` : adset?.name
        }`}
        colSpan={2}
      >
        <ValidatedField errors={errors} name="name">
          <Text w="full">Name</Text>
          <WhiteBgInput
            placeholder="Name"
            {...register(`name`, { required: true })}
          />
        </ValidatedField>
      </Section>

      <Section title="Configuration">
        <ValidatedField errors={errors} name="page_id">
          <Text w="full">Facebook page</Text>
          <Controller
            control={control}
            name="page_id"
            render={({ field }) => (
              <Select
                isDisabled={isLoading}
                value={field.value}
                onChange={(values) => field.onChange(values)}
                chakraStyles={{
                  container: () => ({
                    position: "relative",
                    width: "100%",
                    backgroundColor: "white",
                  }),
                }}
                // @ts-ignore
                options={pages.map((g) => ({
                  label: g.name,
                  value: g.id,
                }))}
              />
            )}
          />
        </ValidatedField>
        <ValidatedField errors={errors} name="instagram_actor_id">
          <Text w="full">Instagram account</Text>
          <Controller
            control={control}
            name="instagram_actor_id"
            render={({ field }) => (
              <Select
                isDisabled={isLoading}
                value={field.value}
                onChange={(values) => field.onChange(values)}
                chakraStyles={{
                  container: () => ({
                    position: "relative",
                    width: "100%",
                    backgroundColor: "white",
                  }),
                }}
                // @ts-ignore
                options={instagramAccounts.map((g) => ({
                  label: g.username,
                  value: g.id,
                }))}
              />
            )}
          />
        </ValidatedField>
      </Section>
      <Section title="Media" colSpan={2}>
        <Controller
          control={control}
          name="media_type"
          render={({ field }) => (
            <RadioGroup
              w="full"
              gap={8}
              value={field.value}
              onChange={(e) => {
                field.onChange(e);

                if (e === "single") {
                  setValue("images", {
                    portrait: [images.portrait[0]].filter(Boolean),
                    square: [images.square[0]].filter(Boolean),
                    // rectangle: [images.rectangle[0]].filter(Boolean),
                  });
                  setValue("videos", {
                    portrait: [videos.portrait[0]].filter(Boolean),
                    square: [videos.square[0]].filter(Boolean),
                    // rectangle: [videos.rectangle[0]].filter(Boolean),
                  });
                }
              }}
            >
              <HStack>
                <Radio backgroundColor={"white"} value="single">
                  Single image/video
                </Radio>
                <Radio backgroundColor={"white"} value="carousel">
                  Carousel
                </Radio>
              </HStack>
            </RadioGroup>
          )}
        />
        <VStack
          minH={"200px"}
          gap={4}
          alignItems={"start"}
          w="full"
          flexWrap={"nowrap"}
        >
          <ValidatedField errors={errors} name="images" hidden={hasAnyVideo}>
            <Text fontWeight={"bold"}>Images</Text>
            <Stack
              direction={media_type === "single" ? "row" : "column"}
              w="full"
              overflowX={["scroll", "auto"]}
            >
              {ratios.map((ratio) => (
                <MediaDisplay
                  onClick={() => {
                    uploadImageRef.current?.click();
                  }}
                  media={images[ratio].map((i) => ({ url: i.picture_url }))}
                  onRemove={(media: { url?: string }) => {
                    setValue(
                      //@ts-ignore
                      `images.${ratio}`,
                      images[ratio].filter((i) => i.picture_url !== media.url),
                      {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      }
                    );
                  }}
                  mediaType={media_type}
                  ratio={ratio}
                />
              ))}
            </Stack>
          </ValidatedField>
          <ValidatedField errors={errors} name="videos" hidden={hasAnyImage}>
            <Text w="full" fontWeight={"bold"}>
              Videos
            </Text>
            <Stack
              direction={media_type === "single" ? "row" : "column"}
              w="full"
            >
              {ratios.map((ratio) => (
                <MediaDisplay
                  type="video"
                  onClick={() => {
                    uploadVideoRef.current?.click();
                  }}
                  media={videos[ratio].map((v) => ({ url: v.source }))}
                  onRemove={(media: { url: string }) => {
                    setValue(
                      //@ts-ignore
                      `videos.${ratio}`,
                      videos[ratio].filter((v) => v.source !== media.url),
                      {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      }
                    );
                  }}
                  ratio={ratio}
                  mediaType={media_type}
                />
              ))}
            </Stack>
          </ValidatedField>
        </VStack>
        <HStack w="full" justifyContent={"flex-start"} gap={4} mt={4}>
          <Button
            hidden={hasAnyVideo || !hasAnyImage}
            isLoading={loading.image || isLoading}
            onClick={() =>
              setValue(
                "images",
                {
                  portrait: [],
                  square: [],
                  // rectangle: [],
                },
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              )
            }
            variant={"destructive"}
          >
            Remove all
          </Button>
          <Button
            hidden={hasAnyImage || !hasAnyVideo}
            isLoading={loading.image || isLoading}
            onClick={() =>
              setValue(
                "videos",
                {
                  portrait: [],
                  square: [],
                  // rectangle: [],
                },
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              )
            }
            variant={"destructive"}
          >
            Remove all
          </Button>
          {loading.image ? (
            <Box flexGrow={1}>
              <Progress
                size="sm"
                w="full"
                rounded={"md"}
                colorScheme={"green"}
                value={uploadProgress}
              />
            </Box>
          ) : null}
        </HStack>

        <FileUploader
          outsideRef={uploadImageRef}
          createUploadUrl={createUploadUrl}
          multiple={media_type === "carousel"}
          accept={"image/*"}
          onError={() => setLoading("image", false)}
          onUploadFinish={async ({ temporaryFileUrl, fileKey, file }) => {
            setLoading("image", false);

            const ratios = await Promise.all(
              requiredImages.map((r) =>
                media_type === "carousel"
                  ? true
                  : validateMultipleAspectRatios(file, r.ratio, 0)
              )
            );

            const indexOfRatio = ratios.findIndex((r) => r === true);

            setRequiredImages((rq) =>
              rq.map((r, i) => {
                return i === indexOfRatio
                  ? { ...r, url: temporaryFileUrl, type: "image" }
                  : r;
              })
            );

            setValue(
              "videos",
              {
                portrait: [],
                square: [],
                // rectangle: [],
              },
              {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true,
              }
            );

            if (media_type === "single") {
              setValue(
                //@ts-ignore
                `images.${requiredImages.at(indexOfRatio).ratioName}`,
                [
                  {
                    picture_url: temporaryFileUrl as string,
                    fileKey,
                    name: "",
                    description: "",
                  },
                ],
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              );
            } else {
              setValue(
                //@ts-ignore
                `images.${requiredImages.at(indexOfRatio).ratioName}`,
                [
                  ...watch(
                    //@ts-ignore
                    `images.square`
                  ),
                  {
                    picture_url: temporaryFileUrl as string,
                    fileKey,
                    name: "",
                    description: "",
                  },
                ],
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              );
            }
          }}
          onProgress={(progress) => setUploadProgress(progress)}
          onStart={async (f, cb) => {
            setLoading("image", true);
            const ratios = await Promise.all(
              requiredImages
                // .filter((r)=> media_type === 'single' ? true : true)
                .map((r) =>
                  media_type === "carousel"
                    ? true
                    : validateMultipleAspectRatios(f, r.ratio, 0.1)
                )
            );

            if (ratios.every((r) => r === false)) {
              toast({
                title: "Invalid aspect ratio",
                description: "The aspect ratio of the image is not valid",
                status: "error",
              });
              setLoading("image", false);
              return;
            }

            cb();
          }}
        />
        <FileUploader
          outsideRef={uploadVideoRef}
          createUploadUrl={createUploadUrl}
          accept={"video/*"}
          onError={() => setLoading("image", false)}
          onUploadFinish={async ({ temporaryFileUrl, fileKey, file }) => {
            setLoading("image", false);

            const ratios = await Promise.all(
              requiredImages.map((r) =>
                media_type === "carousel"
                  ? true
                  : validateMultipleAspectRatios(file, r.ratio, 0.1)
              )
            );

            const indexOfRatio = ratios.findIndex((r) => r === true);

            setRequiredImages((rq) =>
              rq.map((r, i) => {
                return i === indexOfRatio
                  ? { ...r, url: temporaryFileUrl, type: "video" }
                  : r;
              })
            );
            const ratioToName = {
              "1/1": "square",
              "1/1.91": "portrait",
              // "4/5": "rectangle",
            };

            setValue("images", {
              portrait: [],
              square: [],
              // rectangle: [],
            });

            if (media_type === "single") {
              setValue(
                //@ts-ignore
                `videos.${requiredImages.at(indexOfRatio).ratioName}`,
                [
                  {
                    source: temporaryFileUrl as string,
                    fileKey,
                    name: "",
                    description: "",
                    thumbnail_url: "",
                  },
                ],
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              );
            } else {
              setValue(
                //@ts-ignore
                `videos.square`,
                [
                  ...watch(`videos.square`),
                  {
                    source: temporaryFileUrl as string,
                    fileKey,
                    thumbnail_url: "",
                  },
                ],
                {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                }
              );
            }

            console.log("the ratio is", requiredImages.at(indexOfRatio).ratio);
          }}
          onProgress={(progress) => setUploadProgress(progress)}
          onStart={async (f, cb) => {
            setLoading("image", true);
            const ratios = await Promise.all(
              requiredImages
                .filter((r) => !r.url)
                .map((r) =>
                  media_type === "carousel"
                    ? true
                    : validateMultipleAspectRatios(f, r.ratio, 0.1)
                )
            );

            if (ratios.every((r) => r === false)) {
              toast({
                title: "Invalid aspect ratio",
                description: "The aspect ratio of the image is not valid",
                status: "error",
              });
              setLoading("image", false);
              return;
            }

            cb();
          }}
        />
      </Section>
      <Section title="Copy">
        <ValidatedField errors={errors} name="message">
          <Textarea
            isDisabled={isLoading}
            placeholder="Message"
            rows={4}
            backgroundColor={"white"}
            {...register(`message`)}
          />
        </ValidatedField>
      </Section>
      <HStack w="full" justifyContent={"flex-end"}>
        <Button
          onClick={() => {
            back();
            reset();
          }}
          isLoading={isLoading}
          isDisabled={isLoading}
        >
          Back
        </Button>
        <Button
          isLoading={isLoading}
          isDisabled={isLoading || !isValid || !isDirty}
          onClick={handleSubmit(_onSubmit)}
          colorScheme={ad ? "green" : "gray"}
        >
          {ad ? "Save" : "Create"}
        </Button>
      </HStack>
    </VStack>
  );
};

export default AdCreativeForm;
